import { useEffect, useState } from "react";
import axios from "axios";
import { Inbox, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * OwnerInboxBanner — thin owner-only banner that surfaces pending
 * Friends & Supporters submissions across the app. Polls every 60s and
 * only renders when there's at least one pending submission. Dismissible
 * (session-scoped via sessionStorage so it returns on next page open).
 */
export default function OwnerInboxBanner() {
  const { user } = useAuth();
  const [pending, setPending] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  const isOwner = user?.role === "owner";

  // Restore dismissed flag for the current session (per pending count snapshot,
  // so a NEW submission re-shows the banner even after a dismiss)
  useEffect(() => {
    if (!isOwner) return;
    const key = `owner_inbox_dismissed_at_${pending}`;
    setDismissed(sessionStorage.getItem(key) === "1");
  }, [isOwner, pending]);

  useEffect(() => {
    if (!isOwner) return;
    let cancelled = false;

    async function fetchCount() {
      try {
        const { data } = await axios.get(`${API}/admin/supporter-submissions/count`);
        if (!cancelled) setPending(data?.pending || 0);
      } catch {
        // Silent — banner just won't show if the endpoint blips
      }
    }
    fetchCount();
    const t = setInterval(fetchCount, 60_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [isOwner]);

  if (!isOwner || pending === 0 || dismissed) return null;

  function dismiss() {
    sessionStorage.setItem(`owner_inbox_dismissed_at_${pending}`, "1");
    setDismissed(true);
  }

  return (
    <>
      {/* Push the Header (fixed at top-7) and HolidayEggBanner down by the
          banner's height while we're visible. CSS-only via a body class — no
          coupling with Header's own layout code. */}
      <BodyClassEffect className="has-owner-inbox-banner" />
      <style>{`
        body.has-owner-inbox-banner header { top: 4.25rem !important; }
        body.has-owner-inbox-banner .holiday-banner-anchor { top: 7.75rem !important; }
      `}</style>
      <div
        data-testid="owner-inbox-banner"
      className="fixed top-7 left-0 right-0 z-[55] bg-gradient-to-r from-[#4C1D95] via-[#160B22] to-[#4C1D95] border-b-2 border-[#D4AF37]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-3">
        <a
          href="/admin/supporters"
          data-testid="owner-inbox-banner-link"
          className="flex items-center gap-3 text-white hover:text-[#D4AF37] transition-colors flex-1 min-w-0"
        >
          <span className="flex-shrink-0 w-7 h-7 bg-[#D4AF37] text-black flex items-center justify-center font-bold text-xs rounded-full">
            <Inbox size={14} />
          </span>
          <span className="text-[11px] sm:text-xs font-bold uppercase tracking-widest truncate">
            <span className="text-[#D4AF37]">{pending}</span> new{" "}
            {pending === 1 ? "submission" : "submissions"} waiting
            <span className="hidden sm:inline text-neutral-400 normal-case tracking-normal font-normal ml-2">
              · review &amp; approve →
            </span>
          </span>
        </a>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          data-testid="owner-inbox-banner-dismiss"
          className="flex-shrink-0 text-neutral-400 hover:text-white p-1"
        >
          <X size={14} />
        </button>
      </div>
      </div>
    </>
  );
}

function BodyClassEffect({ className }) {
  useEffect(() => {
    document.body.classList.add(className);
    return () => document.body.classList.remove(className);
  }, [className]);
  return null;
}
